import { useState, useEffect, useCallback } from "react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Users, KeyRound } from "lucide-react";

function ReassignDialog({ user, properties, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const matches = properties.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30);
  const assign = async (p) => {
    try {
      await api.put(`/admin/users/${user.id}`, { property_id: p.id });
      toast.success(`${user.name} moved to ${p.name}`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reassign-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Reassign {user.name}</DialogTitle>
          <DialogDescription>Currently: {user.property_name || "no property"}. Pick their correct property.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search properties…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reassign-search" />
        <div className="border border-slate-200 rounded-md divide-y max-h-64 overflow-auto">
          {matches.map((p) => (
            <button key={p.id} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" onClick={() => assign(p)} data-testid={`reassign-option-${p.id}`}>
              {p.name} <span className="text-xs text-slate-400">· {p.location}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [q, setQ] = useState("");
  const [propFilter, setPropFilter] = useState("");
  const [reassigning, setReassigning] = useState(null);
  const [pwResult, setPwResult] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data));
    api.get("/admin/properties").then(({ data }) => setProperties(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}`, { person_role: role });
      toast.success(`${u.name} is now ${role}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: u.active === false });
      toast.success(u.active === false ? `${u.name} reactivated` : `${u.name} deactivated`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setPwResult({ ...data, name: u.name });
    } catch (err) { toast.error(errMsg(err)); }
  };

  const filtered = users.filter((u) =>
    (!q || u.name?.toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()) || (u.username || "").toLowerCase().includes(q.toLowerCase())) &&
    (!propFilter || (u.property_name || "").toLowerCase().includes(propFilter.toLowerCase()))
  );

  return (
    <div>
      <h3 className="font-display font-semibold text-[#1a1f36] flex items-center gap-2 mb-1"><Users className="w-4 h-4 text-[#c8102e]" />All accounts</h3>
      <p className="text-sm text-slate-600 mb-3">Fix mis-mapped imports here: reassign to the right property, switch office/maintenance, deactivate, or hand out a fresh temp password.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input placeholder="Search name, email, username…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="users-search" />
        <Input placeholder="Filter by property…" value={propFilter} onChange={(e) => setPropFilter(e.target.value)} className="max-w-xs" data-testid="users-property-filter" />
        <span className="text-sm text-slate-500 self-center ml-auto" data-testid="users-count">{filtered.length} of {users.length}</span>
      </div>
      <div className="border border-slate-200 rounded-md overflow-x-auto max-h-[600px] overflow-y-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#f8f9fc] text-xs uppercase tracking-wide text-slate-500 sticky top-0">
            <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Login</th><th className="px-3 py-2">Property</th><th className="px-3 py-2">Team</th><th className="px-3 py-2">Last sign-in</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 ${u.active === false ? "opacity-50" : ""}`} data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 font-medium text-[#1a1f36]">{u.name}<div className="text-[10px] uppercase text-slate-400">{u.role}</div></td>
                <td className="px-3 py-2 text-xs font-mono">{u.email || u.username}</td>
                <td className="px-3 py-2 text-xs">{u.property_name || "—"}</td>
                <td className="px-3 py-2">
                  {u.role === "property_user" ? (
                    <Select value={u.person_role || "office"} onValueChange={(v) => setRole(u, v)}>
                      <SelectTrigger className="w-32 h-7 text-xs" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="office">Office</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent>
                    </Select>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{u.last_sign_in ? new Date(u.last_sign_in).toLocaleString() : "never"}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${u.active === false ? "bg-slate-200 text-slate-500" : "bg-green-100 text-green-800"}`}>{u.active === false ? "deactivated" : "active"}</span></td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {u.role === "property_user" && <Button size="sm" variant="ghost" onClick={() => setReassigning(u)} data-testid={`user-reassign-${u.id}`}>Reassign</Button>}
                  <Button size="sm" variant="ghost" onClick={() => resetPw(u)} title="Reset password" data-testid={`user-resetpw-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
                  {u.role !== "admin" && <Button size="sm" variant="ghost" className={u.active === false ? "text-green-700" : "text-red-600"} onClick={() => toggleActive(u)} data-testid={`user-toggle-active-${u.id}`}>{u.active === false ? "Reactivate" : "Deactivate"}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {reassigning && <ReassignDialog user={reassigning} properties={properties} onClose={() => setReassigning(null)} onSaved={() => { setReassigning(null); load(); }} />}
      {pwResult && (
        <Dialog open onOpenChange={(o) => !o && setPwResult(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="pw-reset-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">New temp password for {pwResult.name}</DialogTitle>
              <DialogDescription>Shown once — share it via Teams. They must change it on first sign-in.</DialogDescription>
            </DialogHeader>
            <div className="text-xs text-slate-500">Login: <span className="font-mono">{pwResult.login}</span></div>
            <div className="bg-slate-100 rounded-md p-3 font-mono text-lg text-center" data-testid="pw-reset-temp">{pwResult.temp_password}</div>
            <Button onClick={() => setPwResult(null)} className="w-full" data-testid="pw-reset-done">Done</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
